import { useTranslation } from 'react-i18next';
import { format, formatDistance } from 'date-fns';
import { fr, enUS, es, Locale } from 'date-fns/locale';

/**
 * Custom hook that provides enhanced translation utilities
 * Supports both direct t('key') calls and namespace-based translations
 */
export const useTranslationUtils = () => {
  const { t, i18n } = useTranslation();

  // Locale map for date-fns
  const localeMap: Record<string, Locale> = {
    fr: fr,
    en: enUS,
    es: es,
  };

  /**
   * Translates a key, automatically handles dot notation and namespaces
   * 
   * @param key The translation key (can be in namespace:key format or dot notation)
   * @param options Optional parameters for translation
   * @returns The translated string
   */
  const translate = (key: string, options?: any): string => {
    // Check if the key is missing
    const isMissingKey = (testKey: string, namespace?: string): boolean => {
      const fullKey = namespace ? `${namespace}:${testKey}` : testKey;
      const result = t(fullKey, { ...options, defaultValue: '__NOT_TRANSLATED__' });
      return result === '__NOT_TRANSLATED__';
    };

    // If the key uses dot notation (which isn't supported by i18next directly)
    if (key.includes('.') && !key.includes(':')) {
      const [namespace, actualKey] = key.split('.');
      // If we find a translation for the namespace:key format, use it
      if (!isMissingKey(actualKey, namespace)) {
        return t(`${namespace}:${actualKey}`, options) as string;
      }
      // Otherwise, try the original key (backward compatibility)
      return t(key, options) as string;
    }
    
    // Regular namespace:key format or plain key
    return t(key, options) as string;
  };

  /**
   * Dynamically translates content based on a key or set of mappings
   * Useful for translating dynamic content like reward names
   * 
   * @param content The original content
   * @param keyMap A mapping of original content to translation keys
   * @param fallbackPrefix Optional prefix to use if no mapping is found
   * @returns The translated content
   */
  const tDynamic = (
    content: string, 
    keyMap: Record<string, string> = {}, 
    fallbackPrefix?: string
  ): string => {
    // If the content has a direct mapping, use that key for translation
    if (keyMap[content]) {
      const translationKey = keyMap[content];
      return t(translationKey, { defaultValue: content }) as string;
    }
    
    // If a fallback prefix is provided, try to construct a key
    if (fallbackPrefix) {
      // Check if fallbackPrefix already contains a namespace
      const hasNamespace = fallbackPrefix.includes(':');
      const constructedKey = hasNamespace 
        ? `${fallbackPrefix}.${content.toLowerCase().replace(/\s+/g, '_')}`
        : `${fallbackPrefix}:${content.toLowerCase().replace(/\s+/g, '_')}`;
      
      return t(constructedKey, { defaultValue: content }) as string;
    }
    
    // Return the original content if no translation is found
    return content;
  };

  /**
   * Formats a date according to the current locale
   * 
   * @param date The date to format
   * @param formatString The format to use (see date-fns for options)
   * @returns The date formatted according to the current locale
   */
  const formatDate = (date: Date | number | string, formatString: string = 'PPP') => {
    const currentDate = typeof date === 'string' ? new Date(date) : date;
    const locale = localeMap[i18n.language] || localeMap.fr;
    return format(currentDate, formatString, { locale });
  };

  /**
   * Returns a relative distance between two dates in the current language
   * For example "3 days ago"
   * 
   * @param date The reference date
   * @param baseDate The base date (default: now)
   * @returns The relative distance string
   */
  const formatRelativeTime = (date: Date | number | string, baseDate?: Date | number) => {
    const currentDate = typeof date === 'string' ? new Date(date) : date;
    const locale = localeMap[i18n.language] || localeMap.fr;
    return formatDistance(currentDate, baseDate || new Date(), { locale, addSuffix: true });
  };

  /**
   * Formats a number according to the current locale
   * 
   * @param number The number to format
   * @param options Formatting options (see Intl.NumberFormat)
   * @returns The number formatted according to the current locale
   */
  const formatNumber = (number: number, options?: Intl.NumberFormatOptions) => {
    return new Intl.NumberFormat(i18n.language, options).format(number);
  };

  /**
   * Changes the current language
   * 
   * @param language The language code to use
   */
  const changeLanguage = async (language: string) => {
    await i18n.changeLanguage(language);
    localStorage.setItem("preferredLanguage", language);
  };

  /**
   * Gets the current language
   * 
   * @returns The current language code
   */
  const getCurrentLanguage = () => {
    return i18n.language;
  };

  /**
   * Checks if all translation keys exist
   * Useful for debugging
   * 
   * @param keys Array of keys to check
   * @returns An object with the check results
   */
  const verifyTranslationKeys = (keys: string[]) => {
    const results: Record<string, boolean> = {};
    keys.forEach(key => {
      const translated = t(key, { defaultValue: key });
      results[key] = translated !== key; // If the translation equals the key, it doesn't exist
    });
    return results;
  };

  /**
   * Warns about missing translations in development mode
   * 
   * @param key Translation key to check
   * @param namespace Optional namespace
   */
  const warnMissingTranslation = (key: string, namespace?: string) => {
    if (process.env.NODE_ENV === 'development') {
      const fullKey = namespace ? `${namespace}:${key}` : key;
      const result = t(fullKey, { defaultValue: '__NOT_TRANSLATED__' });
      
      if (result === '__NOT_TRANSLATED__') {
        console.warn(`Missing translation for key: ${fullKey} in language: ${i18n.language}`);
      }
    }
  };

  return {
    t: translate,
    tDynamic,
    formatDate,
    formatRelativeTime,
    formatNumber,
    changeLanguage,
    getCurrentLanguage,
    verifyTranslationKeys,
    warnMissingTranslation,
    i18n
  };
};

export default useTranslationUtils;
